// pages/MissionTaker.jsx

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import MissionTakerTabs from '/components/Tabs/MissionTakerTabs'; // minimal tab layout
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
// Import your dark-mode button styles
import buttonStyles from '/components/Buttons/Buttons.module.css';
import styles from './MissionTaker.module.css';
import { validateMissionSubmission } from '/lib/missionDefinition';

export default function MissionTaker() {
  const router = useRouter();
  const { hash, draftId: draftIdQuery } = router.query;
  const { data: session, status: sessionStatus } = useSession();

  // Mission data & states
  const [missionData, setMissionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tabs
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Naming', 'Description', 'Physics', 'Media'];

  // Form data + missing fields
  const [formData, setFormData] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [workflowMessage, setWorkflowMessage] = useState('');
  const [persistedImages, setPersistedImages] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [claimReady, setClaimReady] = useState(false);
  const [categories, setCategories] = useState([]);
  const autosaveTimer = useRef(null);
  const skipFirstAutosave = useRef(true);

  const formatStoredList = (value) => {
    if (!value) return 'None';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed.length ? parsed.join(', ') : 'None') : value;
    } catch {
      return value;
    }
  };

  // Fetch mission data when hash is ready
  useEffect(() => {
    if (!router.isReady) return;
    setLoading(true);
    setError(null);
    const loadMission = async () => {
      let missionHash = hash;
      let savedPayload = null;
      if (draftIdQuery) {
        const draftResponse = await axios.get(`/api/missions/draft?draftId=${draftIdQuery}`);
        missionHash = draftResponse.data.public_hash;
        savedPayload = draftResponse.data.payload || {};
        try { setPersistedImages(JSON.parse(draftResponse.data.gallery_images || '[]')); } catch { setPersistedImages([]); }
        setDraftId(Number(draftIdQuery));
      }
      return axios.get(`/api/missionTaker/missionTaker?hash=${missionHash}`).then((response) => ({ response, savedPayload }));
    };
    loadMission()
      .then((res) => {
        const mission = res.response.data;
        setMissionData(mission);
        setFormData((current) => ({
          ...current,
          description: mission.description || '',
          difficulty: mission.difficulty ?? 1,
          proposedName: mission.name || '',
          proposedType: mission.type || 'Move',
          ...(res.savedPayload || {}),
        }));
        setLoading(false);
        setInitialized(true);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      });
  }, [draftIdQuery, hash, router.isReady]);

  useEffect(() => {
    axios.get('/api/categories/categories').then((response) => setCategories(response.data.categories || [])).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!missionData?.content_id || sessionStatus !== 'authenticated') return undefined;
    let active = true;
    const sourceContentId = missionData.content_id;
    const reserve = async (method = 'POST') => {
      const response = await fetch('/api/missions/claim', {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceContentId }),
        keepalive: true,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Mission could not be reserved.');
      if (active) setClaimReady(true);
    };
    reserve().catch((claimError) => { if (active) { setClaimReady(false); setWorkflowMessage(claimError.message); } });
    const heartbeat = setInterval(() => reserve('PATCH').catch((claimError) => {
      if (active) { setClaimReady(false); setWorkflowMessage(claimError.message); }
    }), 5 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(heartbeat);
      fetch('/api/missions/claim', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sourceContentId }), keepalive: true }).catch(() => {});
    };
  }, [missionData?.content_id, sessionStatus]);

  const saveDraft = async ({ silent = false } = {}) => {
    if (!session?.user) return setWorkflowMessage('Sign in to save your mission.');
    setSaving(true);
    if (!silent) setWorkflowMessage('');
    try {
      const response = await axios.post('/api/missions/draft', {
        sourceContentId: missionData.content_id,
        draftId,
        formData: { ...formData, galleryImages: [] },
      });
      setDraftId(response.data.draftId);
      setWorkflowMessage(silent ? 'All changes saved.' : 'Draft saved.');
      return response.data.draftId;
    } catch (saveError) {
      setWorkflowMessage(saveError.response?.data?.error || 'Draft could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!initialized || !missionData || !session?.user || submitting) return undefined;
    if (skipFirstAutosave.current) {
      skipFirstAutosave.current = false;
      return undefined;
    }
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setWorkflowMessage('Saving changes…');
    autosaveTimer.current = setTimeout(() => saveDraft({ silent: true }), 900);
    return () => clearTimeout(autosaveTimer.current);
    // File objects are uploaded separately and intentionally excluded from autosave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.alternativeNames, formData.centerGravity, formData.description, formData.difficulty,
    formData.inertia, formData.proposedName, formData.proposedType, formData.weightPlacement,
    formData.categoryIds, formData.perspectiveVideos, initialized]);

  const uploadDraftImages = async (files) => {
    if (!files.length) return;
    const activeDraftId = draftId || await saveDraft({ silent: true });
    if (!activeDraftId) return;
    setWorkflowMessage('Uploading draft media…');
    const request = new FormData();
    files.forEach((file) => request.append('galleryImages', file));
    try {
      const response = await axios.post(`/api/missions/draftMedia?draftId=${activeDraftId}`, request);
      setPersistedImages(response.data.images || []);
      setFormData((current) => ({ ...current, galleryImages: [] }));
      setWorkflowMessage('Media saved with draft.');
    } catch (uploadError) { setWorkflowMessage(uploadError.response?.data?.error || 'Draft media could not be uploaded.'); }
  };

  const removeDraftImage = async (index) => {
    if (!draftId) return;
    const response = await axios.delete(`/api/missions/draftMedia?draftId=${draftId}&index=${index}`);
    setPersistedImages(response.data.images || []);
  };

  const submitMission = async () => {
    if (!claimReady) return setWorkflowMessage('This mission is not currently reserved for you.');
    const validationPayload = { ...formData, galleryImages: [...persistedImages, ...(formData.galleryImages || [])] };
    const allErrors = validateMissionSubmission(missionData.requirements || {}, validationPayload).missing;
    setMissingFields(allErrors);
    if (allErrors.length) return setWorkflowMessage('Complete the required fields before submitting.');
    if (!session?.user) return setWorkflowMessage('Sign in to submit this mission.');
    setSubmitting(true);
    setWorkflowMessage('');
    try {
      const request = new FormData();
      request.append('sourceContentId', missionData.content_id);
      if (draftId) request.append('draftId', draftId);
      const serializable = { ...formData, galleryImages: [] };
      request.append('formData', JSON.stringify(serializable));
      (formData.galleryImages || []).forEach((file) => request.append('galleryImages', file));
      const response = await axios.post('/api/missions/submit', request);
      setWorkflowMessage(response.data.message || 'Mission submitted for review.');
      setTimeout(() => router.push('/Profile'), 900);
    } catch (submitError) {
      setWorkflowMessage(submitError.response?.data?.error || 'Mission could not be submitted.');
    } finally {
      setSubmitting(false);
    }
  };

  // Validate each step
  const validateStep = (stepIndex) => {
    if (!missionData) return true; // If no data, skip validation
    const errors = [];

    // Step 0: Naming
    if (
      stepIndex === 0 &&
      missionData.require_alternative_name_submission &&
      !formData.alternativeNames
    ) {
      errors.push('Alternative Name is required');
    }
    if (stepIndex === 0 && missionData.name_update_required && !formData.proposedName) errors.push('Proposed Name is required');
    if (stepIndex === 0 && missionData.type_required && !formData.proposedType) errors.push('Proposed Content Type is required');
    if (stepIndex === 0 && missionData.category_required && !formData.categoryIds?.length) errors.push('Category is required');
    // Step 1: Description
    if (stepIndex === 1 && missionData.description_required && !formData.description) {
      errors.push('Description is required');
    }
    if (
      stepIndex === 1 &&
      missionData.difficulty_required &&
      (formData.difficulty === '' || formData.difficulty == null)
    ) {
      errors.push('Difficulty is required');
    }
    if (
      stepIndex === 1 &&
      (missionData.difficulty_required || missionData.difficulty_suggested) &&
      formData.difficulty !== '' &&
      formData.difficulty != null &&
      (Number(formData.difficulty) < 1 || Number(formData.difficulty) > 5)
    ) {
      errors.push('Difficulty must be between 1 and 5');
    }
    // Step 2: Physics
    if (stepIndex === 2) {
      if (missionData.center_gravity_required && !formData.centerGravity) {
        errors.push('Center of Gravity is required');
      }
      if (missionData.weight_placement_required && !formData.weightPlacement) {
        errors.push('Weight Placement is required');
      }
      if (missionData.inertia_required && !formData.inertia) {
        errors.push('Inertia is required');
      }
    }
    // Step 3: Media
    if (
      stepIndex === 3 &&
      missionData.gallery_image_update_required &&
      (formData.galleryImages?.length || 0) + persistedImages.length < Number(missionData.num_gallery_images_required || 1)
    ) {
      errors.push(`${missionData.num_gallery_images_required || 1} gallery image(s) required`);
    }
    if (stepIndex === 3) Object.entries(missionData.required_perspectives || {}).forEach(([perspective, count]) => {
      if ((formData.perspectiveVideos?.[perspective] || []).filter(Boolean).length < count) errors.push(`${perspective.replace('_', ' ')} perspective requires ${count} link(s)`);
    });

    setMissingFields(errors);
    return errors.length === 0;
  };

  // Step Navigation
  const nextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };
  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Loading & Error States
  if (loading) {
    return (
      <section className={sectionStyles.section}>
        <div className={`${infoPaneStyles.infoPane} ${styles.missionIntro}`}>
          <h1>Loading Mission...</h1>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={sectionStyles.section}>
        <div className={`${infoPaneStyles.infoPane} ${styles.missionIntro}`}>
          <h1>Error</h1>
          <p>Could not load mission: {error}</p>
        </div>
      </section>
    );
  }

  if (!missionData) {
    return (
      <section className={sectionStyles.section}>
        <div className={`${infoPaneStyles.infoPane} ${styles.missionIntro}`}>
          <h1>Mission Not Found</h1>
          <p>No mission data for hash: {hash}</p>
        </div>
      </section>
    );
  }

  // Main UI
  return (
    <section id="missionTaker" className={sectionStyles.section}>
      {/* Top infoPane with NO mission name */}
      <div className={`${infoPaneStyles.infoPane} ${styles.missionIntro}`}>
        <h1 className={styles.missionTitle}>Mission Taker</h1>
        <p>Make your contribution by filling out all the fields required by the mission. Taking the mission submits your info for review and addition to the database.</p>
        {sessionStatus !== 'loading' && !session?.user && <p className={styles.validationNotice}>Sign in before saving or submitting your work.</p>}
        {/* Name removed as requested */}
        {missingFields.length > 0 && (
          <div className={styles.validationNotice}>
            <strong>Missing Fields:</strong>
            <ul>
              {missingFields.map((field, idx) => (
                <li key={idx}>{field}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={sectionStyles.mainContent}>
        <div className={infoPaneStyles.resizableInfoPane}>
          <MissionTakerTabs
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          >
            {/* Step 0: Naming */}
            {activeStep === 0 && (
              <div className={styles.stepContent}>
                <h2 className={styles.stepHeading}>Naming</h2>
                <div className={styles.readOnlyField}>
                  <span>Name</span>
                  <p>{missionData.name || 'Unnamed content'}</p>
                </div>
                {missionData.name_update_required || missionData.name_update_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>Proposed Name <em>{missionData.name_update_required ? 'Required' : 'Suggested'}</em></span>
                    <input type="text" value={formData.proposedName || ''} onChange={(event) => setFormData({ ...formData, proposedName: event.target.value })} />
                  </label>
                ) : null}
                <div className={styles.readOnlyField}>
                  <span>Alternative Names</span>
                  <p>{formatStoredList(missionData.alternative_names)}</p>
                </div>
                <div className={styles.readOnlyField}>
                  <span>Previous Names</span>
                  <p>{formatStoredList(missionData.previous_names)}</p>
                </div>
                <div className={styles.readOnlyField}>
                  <span>Content Type</span>
                  <p>{missionData.type || 'Not specified'}</p>
                </div>
                {missionData.type_required || missionData.type_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>Proposed Content Type <em>{missionData.type_required ? 'Required' : 'Suggested'}</em></span>
                    <select value={formData.proposedType || missionData.type || 'Move'} onChange={(event) => setFormData({ ...formData, proposedType: event.target.value })}>
                      {['Move', 'Simple Cycle', 'Transition', 'Combination', 'Choreography', 'Position'].map((type) => <option key={type}>{type}</option>)}
                    </select>
                  </label>
                ) : null}
                {missionData.require_alternative_name_submission ||
                missionData.suggest_alternative_name_submission ? (
                  <label className={styles.fieldGroup}>
                    <span>
                      Proposed Alternative Names
                      <em>{missionData.require_alternative_name_submission ? 'Required' : 'Suggested'}</em>
                    </span>
                    <input
                      type="text"
                      placeholder="Enter alternative names"
                      value={formData.alternativeNames || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, alternativeNames: e.target.value })
                      }
                    />
                  </label>
                ) : null}
                {missionData.category_required || missionData.category_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>Proposed Categories <em>{missionData.category_required ? 'Required' : 'Suggested'}</em></span>
                    <select multiple value={(formData.categoryIds || []).map(String)} onChange={(event) => setFormData({ ...formData, categoryIds: [...event.target.selectedOptions].map((option) => Number(option.value)) })}>
                      {categories.map((category) => <option key={category.id} value={category.id}>{category.category_name}</option>)}
                    </select>
                  </label>
                ) : null}
              </div>
            )}

            {/* Step 1: Description */}
            {activeStep === 1 && (
              <div className={styles.stepContent}>
                <h2 className={styles.stepHeading}>Description</h2>
                <div className={styles.readOnlyField}>
                  <span>Current Description</span>
                  <p>{missionData.description || 'No description available'}</p>
                </div>
                <div className={styles.readOnlyField}>
                  <span>Difficulty (1–5)</span>
                  <p>{missionData.difficulty ?? 'Not specified'}</p>
                </div>
                {missionData.difficulty_required || missionData.difficulty_suggested ? (
                  <label className={`${styles.fieldGroup} ${styles.compactNumberField}`}>
                    <span>
                      Proposed Difficulty (1–5)
                      <em>{missionData.difficulty_required ? 'Required' : 'Suggested'}</em>
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="1"
                      value={formData.difficulty ?? 1}
                      onChange={(e) =>
                        setFormData({ ...formData, difficulty: e.target.value })
                      }
                    />
                  </label>
                ) : null}
                {missionData.description_required || missionData.description_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>
                      Proposed Description
                      <em>{missionData.description_required ? 'Required' : 'Suggested'}</em>
                    </span>
                    <textarea
                      placeholder="Enter description"
                      value={formData.description || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                    />
                  </label>
                ) : null}
              </div>
            )}

            {/* Step 2: Physics */}
            {activeStep === 2 && (
              <div className={styles.stepContent}>
                <h2 className={styles.stepHeading}>Physics</h2>
                <div className={styles.readOnlyField}>
                  <span>Center of Gravity</span>
                  <p>{missionData.center_gravity || 'Not specified'}</p>
                </div>
                {missionData.center_gravity_required || missionData.center_gravity_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>
                      Proposed Center of Gravity
                      <em>{missionData.center_gravity_required ? 'Required' : 'Suggested'}</em>
                    </span>
                    <input
                      type="text"
                      placeholder="Describe center of gravity"
                      value={formData.centerGravity || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, centerGravity: e.target.value })
                      }
                    />
                  </label>
                ) : null}
                <div className={styles.readOnlyField}>
                  <span>Weight Placement</span>
                  <p>{missionData.weight_placement || 'Not specified'}</p>
                </div>
                {missionData.weight_placement_required || missionData.weight_placement_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>
                      Proposed Weight Placement
                      <em>{missionData.weight_placement_required ? 'Required' : 'Suggested'}</em>
                    </span>
                    <input
                      type="text"
                      placeholder="Describe weight placement"
                      value={formData.weightPlacement || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, weightPlacement: e.target.value })
                      }
                    />
                  </label>
                ) : null}
                <div className={styles.readOnlyField}>
                  <span>Inertia</span>
                  <p>{missionData.inertia || 'Not specified'}</p>
                </div>
                {missionData.inertia_required || missionData.inertia_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>
                      Proposed Inertia
                      <em>{missionData.inertia_required ? 'Required' : 'Suggested'}</em>
                    </span>
                    <input
                      type="text"
                      placeholder="Describe inertia"
                      value={formData.inertia || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, inertia: e.target.value })
                      }
                    />
                  </label>
                ) : null}
              </div>
            )}

            {/* Step 3: Media */}
            {activeStep === 3 && (
              <div className={styles.stepContent}>
                <h2 className={styles.stepHeading}>Media</h2>
                <div className={styles.readOnlyField}>
                  <span>Main Video Link</span>
                  <p>{missionData.main_video_link || 'None'}</p>
                </div>
                <div className={styles.readOnlyField}>
                  <span>Gallery Images</span>
                  <p>{formatStoredList(missionData.gallery_images)}</p>
                </div>
                {missionData.gallery_image_update_required ||
                missionData.gallery_image_update_suggested ? (
                  <label className={styles.fieldGroup}>
                    <span>
                      New Gallery Images
                      <em>{missionData.gallery_image_update_required ? 'Required' : 'Suggested'}</em>
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => uploadDraftImages(Array.from(e.target.files || []))}
                    />
                  </label>
                ) : null}
                {persistedImages.length > 0 && <div className={styles.savedMedia}>
                  {persistedImages.map((image, index) => <div key={`${image.url}-${index}`}><span>{image.name || image.url}</span><button type="button" onClick={() => removeDraftImage(index)}>Remove</button></div>)}
                </div>}
                {Object.entries(missionData.required_perspectives || {}).map(([perspective, count]) => <label className={styles.fieldGroup} key={perspective}>
                  <span>{perspective.replace('_', ' ')} Perspective Links <em>{count} Required</em></span>
                  <textarea
                    placeholder="One video URL per line"
                    value={(formData.perspectiveVideos?.[perspective] || []).join('\n')}
                    onChange={(event) => setFormData({ ...formData, perspectiveVideos: { ...(formData.perspectiveVideos || {}), [perspective]: event.target.value.split(/\r?\n/) } })}
                  />
                </label>)}
              </div>
            )}
          </MissionTakerTabs>

          {/* Navigation Buttons INSIDE .resizableInfoPane */}
          <div style={{ marginTop: '1rem' }}>
            {activeStep > 0 && (
              <button
                onClick={prevStep}
                className={buttonStyles['dark-button']}
                style={{ marginRight: '1rem' }}
              >
                Back
              </button>
            )}
            {activeStep < steps.length - 1 && (
              <button
                onClick={nextStep}
                className={buttonStyles['dark-button']}
              >
                Next
              </button>
            )}
            <button onClick={() => saveDraft()} disabled={saving || submitting} className={buttonStyles['dark-button']} style={{ marginLeft: '1rem' }}>
              {saving ? 'Saving…' : draftId ? 'Save Draft' : 'Create Draft'}
            </button>
            {activeStep === steps.length - 1 && (
              <button onClick={submitMission} disabled={saving || submitting || !claimReady} className={buttonStyles['dark-button']} style={{ marginLeft: '1rem' }}>
                {submitting ? 'Submitting…' : 'Submit Mission'}
              </button>
            )}
            {workflowMessage && <p className={styles.workflowMessage}>{workflowMessage}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
